import { NO_CATEGORY } from '@shared/constants/categories';
import { useCurrencies } from '@shared/contexts/currencies-context';
import { useOffers } from '@shared/contexts/offers';
import { useUserInfo } from '@shared/contexts/user-info';
import { useWallets } from '@shared/contexts/wallets-context';
import { useConnectWalletButtonStates } from '@shared/hooks/use-connect-wallet-button-states';
import { CurrencyType } from '@shared/types/offers';
import clsx from 'clsx';
import { PowerFiatSwitcher } from 'components/power-fiat-switcher/powerFiatSwithcer';
import { general, icons } from 'constants/assets';
import { url } from 'constants/urls';
import { useTranslation } from 'i18n/useTranslation';
import { FC, useState } from 'react';
import { useTranslation as useTranslationHook } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RouterPathes } from '../../constants/router-paths';
import { LanguageSwitcher } from '../languageSwitcher/LanguageSwitcher';
import { WalletChainButton } from '../walletChainButton/walletChainButton';
import { AccountModalButton } from '../walletConnect/accountModalButton';
import { RenderAuthButton } from './renderAuthButton';
import { RenderBurgerButton } from './renderBurgerButton';
import { RenderPowerButton } from './renderPowerButton';

export const Navbar: FC = () => {
  const { i18n } = useTranslationHook();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: userInfo } = useUserInfo();
  const isLoggedIn = !!userInfo;
  const { navbarTranslations } = useTranslation();

  const returnLoginUrl = window.location.href;
  const redirectLoginUrl = url.redirectLogin(returnLoginUrl);
  const { buttonText, buttonAction, buttonClass } = useConnectWalletButtonStates();
  const { openAccountModal, isConnected, connectedAddress } = useWallets();
  const { selectedCountryInfo, selectedCurrency, setSelectedCurrency } = useCurrencies();

  const [isGuildCategoriesVisible, setIsGuildCategoriesVisible] = useState(false);
  const [isRegularCategoriesVisible, setIsRegularCategoriesVisible] = useState(false);

  const { buttonsTranslations } = useTranslation();
  const {
    isGuildFounder,
    availableCategories,
    availableGuildCategories,
    selectedOfferType,
    setSelectedOfferType,
    setSelectedCategory,
  } = useOffers();

  const isRegularOfferType = selectedOfferType === 'regular';

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
    setIsRegularCategoriesVisible(false);
    setIsGuildCategoriesVisible(false);
  };

  const handleCurrencyChange = (currency: CurrencyType) => {
    setSelectedCurrency(currency);
    setSelectedCategory(undefined);
  };

  const navbarItems = [
    // {
    //   label: navbarTranslations.powerChests,
    //   href: import.meta.env.VITE_POWER_CHESTS_URL,
    // },
    // {
    //   label: navbarTranslations.guilds,
    //   href: import.meta.env.VITE_GUILDS_URL,
    // },
    {
      label: navbarTranslations.staking,
      href: import.meta.env.VITE_STAKING_URL,
    },
  ];

  const getCategoryName = (category: string) => (category === NO_CATEGORY
    ? i18n.t('offers.noCategory', { defaultValue: 'No Category' })
    : i18n.t(`offers.category.${category}`, { defaultValue: category }));

  const renderWalletButton = () => (
    <>
      {userInfo && !isConnected && (
        <button
          type="button"
          className={clsx(buttonClass, 'yellow-button font-border poppins-2xl my-3 h-[50px] w-full flex-1 text-[17.5px] uppercase transition-all duration-200 hover:brightness-125 sm:h-[42px] sm:text-[17.5px] lg:my-0 lg:px-2 lg:text-[12.5px] xl:px-4 xl:text-[17.5px]')}
          onClick={buttonAction}
        >
          {buttonText}
        </button>
      )}
      {userInfo && isConnected && connectedAddress && (
        <AccountModalButton openAccountModal={openAccountModal} address={connectedAddress} className="my-3 lg:my-0" />
      )}
    </>
  );

  const renderMobileMenuList = () => (
    <ul
      className={clsx(
        'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 bg-shell absolute left-0 top-[72px] z-50 w-full overflow-y-auto rounded-b-sm px-4 uppercase transition-all duration-300 ease-in-out',
        {
          'max-h-[calc(100vh-72px)] opacity-100': isMobileMenuOpen,
          'max-h-0 opacity-0': !isMobileMenuOpen,
        },
      )}
    >
      <li className="flex w-full font-bold">{RenderPowerButton()}</li>
      {RenderAuthButton({ isLoggedIn, redirectLoginUrl, toggleMobileMenu })}
      {navbarItems.map((item) => (
        <li key={item.label} className="flex w-full">
          <Link
            to={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="border-separator font-poppins flex h-[64px] w-full items-center border-b text-[17.5px] font-black"
          >
            {item.label}
          </Link>
        </li>
      ))}
      {
        isGuildFounder && !!availableGuildCategories.length && (
          <li key="guild-offers">
            <button
              type="button"
              className={clsx('border-separator font-poppins flex h-[64px] w-full items-center justify-between border-b text-[17.5px] font-black', selectedOfferType === 'guild' && 'text-neon_yellow')}
              onClick={() => {
                setIsGuildCategoriesVisible(!isGuildCategoriesVisible);
              }}
            >
              {buttonsTranslations.guildOffers}
              <img
                src={!isRegularOfferType ? icons.chevronDownYellow : icons.chevronDown}
                alt="chevron-down"
                className={clsx('mr-4 rotate-180 transition-all duration-200', isGuildCategoriesVisible && 'rotate-0')}
              />
            </button>
          </li>
        )
      }
      {
        isGuildCategoriesVisible && availableGuildCategories.map((category) => (
          <li key={category}>
            <button
              type="button"
              className="border-separator font-poppins flex h-[64px] w-full items-center border-b pl-6 text-[17.5px] font-black"
              onClick={() => {
                setSelectedOfferType('guild');

                setTimeout(() => {
                  setSelectedCategory(category);
                  toggleMobileMenu();
                }, 100);
              }}
            >
              {getCategoryName(category)}
            </button>
          </li>
        ))
      }
      {
        !!availableCategories.length && (
          <li key="regular-offers">
            <button
              type="button"
              className={clsx(
                'border-separator font-poppins flex h-[64px] w-full items-center justify-between border-b text-[17.5px] font-black',
                isRegularOfferType && 'text-neon_yellow',
              )}
              onClick={() => {
                setIsRegularCategoriesVisible(!isRegularCategoriesVisible);
              }}
            >
              {buttonsTranslations.regularOffers}
              <img
                src={isRegularOfferType ? icons.chevronDownYellow : icons.chevronDown}
                alt="chevron-down"
                className={clsx('mr-4 rotate-180 transition-all duration-200', isRegularCategoriesVisible && 'rotate-0')}
              />
            </button>
          </li>
        )
      }
      {
        isRegularCategoriesVisible && availableCategories.map((category) => (
          <li key={category}>
            <button
              type="button"
              className="border-separator font-poppins flex h-[64px] w-full items-center border-b pl-6 text-[17.5px] font-black"
              onClick={() => {
                setSelectedOfferType('regular');

                setTimeout(() => {
                  setSelectedCategory(category);
                  toggleMobileMenu();
                }, 100);
              }}
            >
              {getCategoryName(category)}
            </button>
          </li>
        ))
      }
      <div className={clsx(
        isLoggedIn && isConnected && connectedAddress && 'border-separator border-b',
      )}
      >
        {renderWalletButton()}
      </div>
      {isLoggedIn && isConnected && connectedAddress && (
        <WalletChainButton type="navbar" />
      )}
      <PowerFiatSwitcher
        className="my-4 w-full"
        countryInfo={selectedCountryInfo}
        onCurrencyChange={handleCurrencyChange}
        value={selectedCurrency}
      />
    </ul>
  );

  const renderMenuListDesktop = () => (
    <ul className="flex flex-row items-center gap-4">
      <LanguageSwitcher />
      <li className="flex font-bold">{RenderPowerButton()}</li>
      {isLoggedIn && isConnected && connectedAddress && (
        <WalletChainButton type="navbar" />
      )}
      {renderWalletButton()}
      {RenderAuthButton({ isLoggedIn, redirectLoginUrl, toggleMobileMenu })}
    </ul>
  );

  return (
    <header
      className="bg-shell z-50 flex items-center justify-between px-6 py-4 md:px-4 lg:px-10"
    >
      <div className="mid:mr-4 mid:gap-4 flex flex-row items-center gap-2">
        <Link to={RouterPathes.home}>
          <div className="flex h-[40px] items-center gap-4 md:h-[58px]">
            <img
              src={general.fabLogo}
              alt="fab-logo"
              className="w-[63.45px] md:h-[43.49px]  md:w-[75.45px]"
            />
            <div className="font-poppins hover:text-neon_yellow font-black uppercase md:block lg:hidden lg:text-[12.5px] xl:block xl:text-[17.5px]">
              {navbarTranslations.official}
              <br />
              {navbarTranslations.store}
            </div>
            <div className="bg-separator h-[36px] w-[2px] md:h-[43px]" />
          </div>
        </Link>
        {navbarItems.map((item) => (
          <Link
            key={item.label}
            to={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-poppins hover:text-neon_yellow hidden font-black uppercase lg:block lg:text-[12.5px] xl:text-[17.5px]"
          >
            {item.label}
          </Link>
        ))}
      </div>
      <div className="flex items-center">
        <div className="flex flex-row items-center gap-4 lg:hidden">
          <LanguageSwitcher />
          <RenderBurgerButton
            toggleMobileMenu={toggleMobileMenu}
            isMobileMenuOpen={isMobileMenuOpen}
          />
          {renderMobileMenuList()}
        </div>
        <div className="hidden lg:block">{renderMenuListDesktop()}</div>
      </div>
    </header>
  );
};
